// ─── Analytics (22/10/2026) ──────────────────────────────────────────────────
//
// 📊 Mockup «Pacchetto 1 · la sezione Analytics» (v2), approvato: *«1A 2B»*. Un
// capitolo suo nel menu, sotto «lezioni» (1A), e la vede solo chi gestisce gli
// utenti (2B, `view_admin_utenti`). Mostra quello che il gestionale annota per
// Claude Duet, un giorno alla volta: da dove arrivano le prenotazioni, le ricerche
// del bot, i rifiuti, la storia delle partite aperte. Legge e basta.
// Lo studio intero: `docs/funzioni/analytics-duet.md`.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ETICHETTE_AVVISO_DEL_BOT,
  ETICHETTE_GESTO_DEL_PONTE,
  ETICHETTE_ORIGINE,
  ETICHETTE_RICERCA,
  ETICHETTE_VOCE_DEL_BOT,
  etichettaDelBot,
  type EsitoAttesaDelBot,
  type EsitoInvitoDelBot,
  type GiornoAnalytics,
  type Prenotazione,
} from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { giornoBreve, giornoPerEsteso, oggi, piuGiorni } from '../../lib/time.js';
import { Attesa, Guasto } from '../../components/pezzi.js';
import { etichettaTipo } from '../../features/calendario/Griglia.js';
import { Conversazioni, Fotografia, Proposte } from './Duet.js';

const FUSO = 'Europe/Rome';
const LINGUETTE = [
  ['prenotazioni', 'Da dove arrivano'],
  ['ricerche', 'Ricerche'],
  ['rifiuti', 'Rifiuti'],
  ['partite', 'Partite aperte'],
  ['bot', 'Dal bot'],
  // 🤖 Pacchetto 3 (mockup «La porta per Duet» v4): la fotografia (2A), le conversazioni (3B), le proposte (4A).
  ['fotografia', 'Fotografia'],
  ['conversazioni', 'Conversazioni'],
  ['proposte', 'Proposte di Duet'],
] as const;
type Linguetta = (typeof LINGUETTE)[number][0];

/** `18:40`, in ora di Roma. */
const ora = (iso: string) =>
  new Intl.DateTimeFormat('it-IT', { timeZone: FUSO, hour: '2-digit', minute: '2-digit' }).format(
    new Date(iso),
  );
/** `sab 25/10`, in ora di Roma. */
const giornoDi = (iso: string) =>
  giornoBreve(new Intl.DateTimeFormat('sv-SE', { timeZone: FUSO }).format(new Date(iso)));

const PASSAGGIO: Record<GiornoAnalytics['partiteAperte'][number]['eventi'][number]['event'], string> = {
  aperta: 'Aperta',
  chiusa: 'Richiusa',
  entra: 'Entra',
  esce: 'Esce',
  piena: 'Piena, si chiude da sola',
};
const mancano = (n: number | null) =>
  n === null ? '' : n === 1 ? ' · manca 1' : n > 1 ? ` · mancano ${n}` : '';

// 🤖 Pacchetto 2 (*«1A 2A 3B»*): com'è finito un invito, e un posto in lista d'attesa.
const ESITO_INVITO: Record<EsitoInvitoDelBot, string> = {
  accettato: 'ha accettato',
  rifiutato: 'ha rifiutato',
  ritirato: 'invito ritirato',
  posto_preso: 'posto già preso',
  non_recapitato: 'non arrivato',
};
const ESITO_ATTESA: Record<EsitoAttesaDelBot, string> = {
  entrato: 'ora in campo',
  uscito: 'fuori dalla lista',
  chiusa: 'partita chiusa',
};
const conta = <T,>(righe: T[], vale: (r: T) => boolean) => righe.filter(vale).length;

export function Analytics(): React.JSX.Element {
  const [giorno, setGiorno] = useState(oggi());
  const [linguetta, setLinguetta] = useState<Linguetta>('prenotazioni');
  const dati = useQuery({
    queryKey: ['analytics-giorno', giorno],
    queryFn: () => chiama<GiornoAnalytics>('GET', `/analytics/giorno?date=${giorno}`),
  });
  const d = dati.data;

  return (
    <section className="analytics">
      <header className="testata analytics-testata">
        <h2>Analytics</h2>
        <div className="analytics-giorno">
          <button
            type="button"
            className="bordato"
            aria-label="Giorno prima"
            onClick={() => setGiorno(piuGiorni(giorno, -1))}
          >
            ‹
          </button>
          <strong>{giornoPerEsteso(giorno)}</strong>
          <button
            type="button"
            className="bordato"
            aria-label="Giorno dopo"
            disabled={giorno >= oggi()}
            onClick={() => setGiorno(piuGiorni(giorno, 1))}
          >
            ›
          </button>
        </div>
      </header>
      <p className="tenue piccolo">
        Quello che il gestionale annota ogni giorno, per capire come riempire i campi. Ricerche e rifiuti si
        annotano senza il nome di chi li ha fatti.
      </p>

      <Guasto errore={dati.error} />
      {dati.isPending && <Attesa cosa="i dati del giorno" />}
      {d && (
        <>
          <div className="analytics-numeri">
            <div>
              <b>{d.prenotazioni.length}</b>
              <span>
                prenotazioni nate, {d.prenotazioni.filter((p) => p.origin === 'bot').length} dal bot
              </span>
            </div>
            <div>
              <b>{d.ricerche.length}</b>
              <span>
                ricerche dal bot, {d.ricerche.filter((r) => r.results === 0).length} senza risultato
              </span>
            </div>
            <div>
              <b>{d.rifiuti.length}</b>
              <span>rifiuti al bot</span>
            </div>
            <div>
              <b>{d.partiteAperte.length}</b>
              <span>
                partite aperte,{' '}
                {d.partiteAperte.filter((p) => p.eventi.some((e) => e.event === 'piena')).length} riempite
              </span>
            </div>
            <div>
              <b>{d.dalBot.arrivato ? d.dalBot.avvisi.reduce((n, a) => n + a.sent, 0) : '—'}</b>
              <span>
                {d.dalBot.arrivato
                  ? `avvisi mandati, ${d.dalBot.avvisi.reduce((n, a) => n + a.touched, 0)} toccati`
                  : 'avvisi del bot: arrivano la notte dopo'}
              </span>
            </div>
          </div>

          <div className="linguette-campi" role="tablist">
            {LINGUETTE.map(([chiave, testo]) => (
              <button
                key={chiave}
                type="button"
                role="tab"
                aria-selected={linguetta === chiave}
                className={linguetta === chiave ? 'scelta' : ''}
                onClick={() => setLinguetta(chiave)}
              >
                {testo}
              </button>
            ))}
          </div>

          {linguetta === 'prenotazioni' &&
            (d.prenotazioni.length === 0 ? (
              <p className="tenue">Quel giorno non è nata nessuna prenotazione.</p>
            ) : (
              <div className="tabella-scorre">
                <table>
                  <thead>
                    <tr>
                      <th>Nata alle</th>
                      <th>Campo e ora</th>
                      <th>Tipo</th>
                      <th>Arriva da</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.prenotazioni.map((p) => (
                      <tr key={p.id}>
                        <td>{ora(p.createdAt)}</td>
                        <td>
                          {p.courtName} · {giornoDi(p.startsAt)} · {ora(p.startsAt)}
                        </td>
                        <td>{etichettaTipo(p.type as Prenotazione['type']) ?? p.type}</td>
                        <td>
                          <span className={`analytics-origine ${p.origin}`}>
                            {ETICHETTE_ORIGINE[p.origin]}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}

          {linguetta === 'ricerche' &&
            (d.ricerche.length === 0 ? (
              <p className="tenue">Quel giorno il bot non ha fatto ricerche.</p>
            ) : (
              <div className="tabella-scorre">
                <table>
                  <thead>
                    <tr>
                      <th>Alle</th>
                      <th>Cosa cercava</th>
                      <th>Per il giorno</th>
                      <th>Risultati</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.ricerche.map((r, i) => (
                      <tr key={i} className={r.results === 0 ? 'analytics-a-vuoto' : ''}>
                        <td>{ora(r.at)}</td>
                        <td>
                          {ETICHETTE_RICERCA[r.kind]}
                          {r.durationMinutes ? ` · ${r.durationMinutes} min` : ''}
                        </td>
                        <td>
                          {r.forDate === null
                            ? '—'
                            : r.toDate && r.toDate !== r.forDate
                              ? `dal ${giornoBreve(r.forDate)} al ${giornoBreve(r.toDate)}`
                              : giornoBreve(r.forDate)}
                        </td>
                        <td>{r.results}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}

          {linguetta === 'rifiuti' &&
            (d.rifiuti.length === 0 ? (
              <p className="tenue">Quel giorno il gestionale ha detto sempre di sì al bot.</p>
            ) : (
              <div className="tabella-scorre">
                <table>
                  <thead>
                    <tr>
                      <th>Alle</th>
                      <th>Gesto</th>
                      <th>Esito</th>
                      <th>Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.rifiuti.map((r, i) => (
                      <tr key={i}>
                        <td>{ora(r.at)}</td>
                        <td>{ETICHETTE_GESTO_DEL_PONTE[r.action]}</td>
                        <td>{r.outcome === 'rifiutato' ? 'Rifiutato' : 'Non lo so ancora'}</td>
                        <td>{r.reason ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}

          {linguetta === 'partite' &&
            (d.partiteAperte.length === 0 ? (
              <p className="tenue">Quel giorno nessuna partita aperta ha cambiato stato.</p>
            ) : (
              <div className="analytics-partite">
                {d.partiteAperte.map((p) => (
                  <div key={p.bookingId} className="analytics-partita">
                    <strong>
                      {p.courtName} · {giornoDi(p.startsAt)} · {ora(p.startsAt)}
                      {p.matchType ? ` · ${p.matchType}` : ''}
                      {p.organizzatore ? ` · organizza ${p.organizzatore}` : ''}
                    </strong>
                    <ol>
                      {p.eventi.map((e, i) => (
                        <li key={i}>
                          <time>
                            {giornoDi(e.at)} {ora(e.at)}
                          </time>
                          <span>
                            {PASSAGGIO[e.event]}
                            {e.chi ? ` ${e.chi}` : ''}
                            {e.event === 'piena' ? '' : mancano(e.freePlaces)}
                          </span>
                        </li>
                      ))}
                    </ol>
                  </div>
                ))}
              </div>
            ))}

          {linguetta === 'bot' && <DalBot dati={d.dalBot} />}
          {linguetta === 'fotografia' && <Fotografia giorno={giorno} />}
          {linguetta === 'conversazioni' && <Conversazioni giorno={giorno} />}
          {linguetta === 'proposte' && <Proposte />}
        </>
      )}
    </section>
  );
}

/** 🤖 La linguetta «Dal bot»: quello che sa solo il bot, arrivato la notte dopo (1A). */
function DalBot({ dati }: { dati: GiornoAnalytics['dalBot'] }): React.JSX.Element {
  const massimo = Math.max(1, ...dati.tocchi.map((t) => t.taps));
  return (
    <div className="analytics-dal-bot">
      <p className="tenue piccolo">
        {dati.arrivato
          ? 'I totali di quel giorno, mandati dal bot la notte dopo.'
          : 'I totali di quel giorno arrivano dal bot la notte dopo: qui sotto, intanto, inviti e lista d’attesa già arrivati.'}
      </p>

      <h3>I bottoni toccati</h3>
      {dati.tocchi.length === 0 ? (
        <p className="tenue">Per quel giorno il bot non ha ancora mandato i tocchi.</p>
      ) : (
        <div className="tabella-scorre">
          <table>
            <thead>
              <tr>
                <th>Voce</th>
                <th>Tocchi</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {dati.tocchi.map((t) => (
                <tr key={t.item}>
                  <td>{etichettaDelBot(ETICHETTE_VOCE_DEL_BOT, t.item)}</td>
                  <td>{t.taps}</td>
                  <td>
                    <span
                      className="analytics-barra"
                      style={{ width: `${Math.max(2, Math.round((t.taps / massimo) * 140))}px` }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3>Gli avvisi</h3>
      {dati.avvisi.length === 0 ? (
        <p className="tenue">Per quel giorno il bot non ha ancora mandato gli avvisi.</p>
      ) : (
        <div className="tabella-scorre">
          <table>
            <thead>
              <tr>
                <th>Avviso</th>
                <th>Mandati</th>
                <th>Toccati</th>
              </tr>
            </thead>
            <tbody>
              {dati.avvisi.map((a) => (
                <tr key={a.family} className={a.sent > 0 && a.touched === 0 ? 'analytics-a-vuoto' : ''}>
                  <td>{etichettaDelBot(ETICHETTE_AVVISO_DEL_BOT, a.family)}</td>
                  <td>{a.sent}</td>
                  <td>{a.touched}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3>Gli inviti a una partita</h3>
      {dati.inviti.length === 0 ? (
        <p className="tenue">Quel giorno nessun socio ha invitato un altro a una partita.</p>
      ) : (
        <div className="tabella-scorre">
          <table>
            <thead>
              <tr>
                <th>Partita</th>
                <th>Mandati</th>
                <th>Accettati</th>
                <th>Rifiutati</th>
                <th>Senza risposta</th>
                <th>Posto già preso</th>
              </tr>
            </thead>
            <tbody>
              {dati.inviti.map((p) => (
                <tr key={p.bookingId}>
                  <td>
                    {p.courtName} · {giornoDi(p.startsAt)} · {ora(p.startsAt)}
                    {p.organizzatore ? ` · organizza ${p.organizzatore}` : ''}
                    <div className="analytics-chi">
                      {p.righe
                        .map(
                          (r) =>
                            `${r.a ?? 'Un socio'} ${r.outcome ? ESITO_INVITO[r.outcome] : 'non ha ancora risposto'}`,
                        )
                        .join(' · ')}
                    </div>
                  </td>
                  <td>{p.righe.length}</td>
                  <td>{conta(p.righe, (r) => r.outcome === 'accettato')}</td>
                  <td>{conta(p.righe, (r) => r.outcome === 'rifiutato')}</td>
                  <td>{conta(p.righe, (r) => r.outcome === null)}</td>
                  <td>{conta(p.righe, (r) => r.outcome === 'posto_preso')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3>La lista d’attesa</h3>
      {dati.attesa.length === 0 ? (
        <p className="tenue">Quel giorno nessuno si è messo in lista d’attesa.</p>
      ) : (
        <div className="tabella-scorre">
          <table>
            <thead>
              <tr>
                <th>Partita</th>
                <th>In lista</th>
                <th>Entrati</th>
                <th>Usciti dalla lista</th>
                <th>Partita chiusa</th>
              </tr>
            </thead>
            <tbody>
              {dati.attesa.map((p) => (
                <tr key={p.bookingId}>
                  <td>
                    {p.courtName} · {giornoDi(p.startsAt)} · {ora(p.startsAt)}
                    {p.organizzatore ? ` · organizza ${p.organizzatore}` : ''}
                    <div className="analytics-chi">
                      {p.righe
                        .map(
                          (r) =>
                            `${r.chi ?? 'Un socio'} ${r.outcome ? ESITO_ATTESA[r.outcome] : 'ancora in lista'}`,
                        )
                        .join(' · ')}
                    </div>
                  </td>
                  <td>{p.righe.length}</td>
                  <td>{conta(p.righe, (r) => r.outcome === 'entrato')}</td>
                  <td>{conta(p.righe, (r) => r.outcome === 'uscito')}</td>
                  <td>{conta(p.righe, (r) => r.outcome === 'chiusa')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
